import { z } from 'zod';
import { SLOT_STATUSES } from '../enums';
import { idSchema, isoDateSchema, isoTimeSchema } from './common';
// =============================================================================
// Modeles de planification
// -----------------------------------------------------------------------------
// Exige par l'enonce : « Afficher, modifier et ajouter des modeles de planifications ».
// Un modele est un generateur de creneaux : il decrit le rythme de travail d'un
// technicien sur une zone et une periode, et produit les `Slot` correspondants.
// =============================================================================
export const createScheduleTemplateSchema = z
    .object({
    name: z.string().trim().min(2, 'Nom requis.').max(100),
    technicianId: idSchema,
    zoneId: idSchema,
    /** Jours travailles, au format ISO-8601 (1 = lundi ... 7 = dimanche). */
    weekdays: z
        .array(z.union([
        z.literal(1),
        z.literal(2),
        z.literal(3),
        z.literal(4),
        z.literal(5),
        z.literal(6),
        z.literal(7),
    ]))
        .min(1, 'Selectionnez au moins un jour.')
        .max(7)
        .refine((days) => new Set(days).size === days.length, {
        message: 'Un meme jour ne peut pas etre selectionne deux fois.',
    }),
    startTime: isoTimeSchema,
    endTime: isoTimeSchema,
    /**
     * Granularite du creneau elementaire, en minutes. Le cadrage metier a retenu
     * 35 a 45 minutes ; un forfait plus long consomme plusieurs creneaux (RG-03).
     */
    slotDurationMin: z
        .number()
        .int()
        .min(15, 'Granularite minimale : 15 minutes.')
        .max(240, 'Granularite maximale : 4 heures.'),
    /** Pause dejeuner optionnelle : aucun creneau n'est genere sur cet intervalle. */
    breakStartTime: isoTimeSchema.optional().nullable(),
    breakEndTime: isoTimeSchema.optional().nullable(),
    validFrom: isoDateSchema,
    validTo: isoDateSchema,
    isActive: z.boolean().default(true),
})
    .refine((data) => data.startTime < data.endTime, {
    message: "L'heure de fin doit etre posterieure a l'heure de debut.",
    path: ['endTime'],
})
    .refine((data) => data.validFrom <= data.validTo, {
    message: 'La date de fin de validite doit etre posterieure a la date de debut.',
    path: ['validTo'],
})
    .refine((data) => (!data.breakStartTime && !data.breakEndTime) ||
    (!!data.breakStartTime && !!data.breakEndTime && data.breakStartTime < data.breakEndTime), {
    message: 'La pause doit avoir un debut et une fin coherents.',
    path: ['breakEndTime'],
})
    .refine((data) => !data.breakStartTime ||
    (data.breakStartTime >= data.startTime && (data.breakEndTime ?? '') <= data.endTime), {
    message: 'La pause doit etre comprise dans la plage de travail.',
    path: ['breakStartTime'],
});
/**
 * Zod n'autorise pas `.partial()` sur un objet deja affine par `.refine()`.
 * Le schema de mise a jour est donc redeclare explicitement.
 */
export const updateScheduleTemplateSchema = z.object({
    name: z.string().trim().min(2).max(100).optional(),
    technicianId: idSchema.optional(),
    zoneId: idSchema.optional(),
    weekdays: z.array(z.number().int().min(1).max(7)).min(1).max(7).optional(),
    startTime: isoTimeSchema.optional(),
    endTime: isoTimeSchema.optional(),
    slotDurationMin: z.number().int().min(15).max(240).optional(),
    breakStartTime: isoTimeSchema.nullable().optional(),
    breakEndTime: isoTimeSchema.nullable().optional(),
    validFrom: isoDateSchema.optional(),
    validTo: isoDateSchema.optional(),
    isActive: z.boolean().optional(),
});
export const scheduleTemplateSchema = z.object({
    id: idSchema,
    name: z.string(),
    technicianId: idSchema,
    technician: z.object({ id: idSchema, firstName: z.string(), lastName: z.string() }).optional(),
    zoneId: idSchema,
    zone: z.object({ id: idSchema, name: z.string(), color: z.string() }).optional(),
    weekdays: z.array(z.number().int()),
    startTime: z.string(),
    endTime: z.string(),
    slotDurationMin: z.number().int(),
    breakStartTime: z.string().nullable(),
    breakEndTime: z.string().nullable(),
    validFrom: z.string(),
    validTo: z.string(),
    isActive: z.boolean(),
});
// --- Generation de creneaux ---------------------------------------------------
export const generateSlotsSchema = z
    .object({
    from: isoDateSchema,
    to: isoDateSchema,
    /**
     * `true` = simulation seule, aucune ecriture. L'interface administrateur
     * previsualise systematiquement avant d'appliquer.
     */
    dryRun: z.boolean().default(true),
})
    .refine((data) => data.from <= data.to, {
    message: 'La date de fin doit etre posterieure a la date de debut.',
    path: ['to'],
});
export const generateSlotsResultSchema = z.object({
    dryRun: z.boolean(),
    /** Creneaux qui seront (ou ont ete) crees. */
    created: z.number().int(),
    /** Creneaux deja presents, ignores : la generation est idempotente. */
    skippedExisting: z.number().int(),
    /** Creneaux non generes car ils chevauchaient une intervention deja reservee. */
    skippedConflict: z.number().int(),
    firstSlotAt: z.string().nullable(),
    lastSlotAt: z.string().nullable(),
    /** Echantillon des premiers creneaux, pour la previsualisation. */
    preview: z.array(z.object({ startsAt: z.string(), endsAt: z.string() })).max(50),
});
// =============================================================================
// Creneaux
// =============================================================================
export const slotSchema = z.object({
    id: idSchema,
    startsAt: z.string(),
    endsAt: z.string(),
    status: z.enum(SLOT_STATUSES),
    technicianId: idSchema,
    technician: z.object({ id: idSchema, firstName: z.string(), lastName: z.string() }).optional(),
    zoneId: idSchema,
    zone: z.object({ id: idSchema, name: z.string(), color: z.string() }).optional(),
    interventionId: idSchema.nullable(),
});
export const slotQuerySchema = z.object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    technicianId: idSchema.optional(),
    zoneId: idSchema.optional(),
    status: z.enum(SLOT_STATUSES).optional(),
});
/** Blocage manuel d'une plage par l'administrateur (conge, formation, meteo). */
export const blockSlotsSchema = z.object({
    technicianId: idSchema,
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
    reason: z.string().trim().min(3, 'Motif requis.').max(255),
});
// =============================================================================
// Moteur de disponibilite (RG-02 + RG-03)
// =============================================================================
export const availabilityQuerySchema = z
    .object({
    /** Coordonnees issues du geocodage BAN de l'adresse saisie par le client. */
    lat: z.coerce.number().min(-90).max(90),
    lng: z.coerce.number().min(-180).max(180),
    packageId: idSchema,
    from: isoDateSchema,
    to: isoDateSchema,
})
    .refine((data) => data.from <= data.to, {
    message: 'La date de fin doit etre posterieure a la date de debut.',
    path: ['to'],
});
/**
 * Un depart possible : l'heure a laquelle l'intervention peut commencer, et la liste
 * des creneaux elementaires consecutifs qu'elle consommera.
 */
export const availabilityOptionSchema = z.object({
    startsAt: z.string(),
    endsAt: z.string(),
    technicianId: idSchema,
    technicianName: z.string(),
    /** Identifiants des creneaux a reserver, dans l'ordre chronologique. */
    slotIds: z.array(idSchema).min(1),
});
export const availabilityResponseSchema = z.object({
    zone: z.object({ id: idSchema, name: z.string(), color: z.string() }),
    packageDurationMin: z.number().int(),
    /** Nombre de creneaux elementaires necessaires, calcule par `slotsRequired()`. */
    slotsRequired: z.number().int(),
    /** Departs possibles regroupes par jour, pour l'affichage calendaire. */
    days: z.array(z.object({
        date: z.string(),
        options: z.array(availabilityOptionSchema),
    })),
    totalOptions: z.number().int(),
});
//# sourceMappingURL=scheduling.js.map